import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState, ChangeEvent, FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ReactMarkdown from 'react-markdown';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ulid } from 'ulidx';
import { StepperInput } from '@/components/ui/stepper-input';
import { Money } from '@/lib/money';
import { useLanguage } from '@/providers/LanguageProvider';

interface MoneyValues {
  amount: number;
  currency: string;
}

interface FormFee {
  id: string;
  required: boolean;
  fee: {
    name: string;
    amountPerUnit: MoneyValues | null;
    percentage: number | null;
  };
}

interface FormConfig {
  id: string;
  eventName: string;
  instructions: string;
  max_amount_for_guests_per_request: number;
  disabled: boolean;
  disabled_reason: string;
  feeDescription?: string | null;
  fees?: FormFee[];
}

interface FormData {
  name: string;
  email: string;
  places: number;
  requestedTrough: string;
  message: string;
}

interface SubmittedFee {
  name: string;
  amountPerUnit: MoneyValues;
  required: boolean;
}

interface SubmittedRequest {
  submittedAt: string;
  eventName: string;
  name: string;
  email: string;
  places: number;
  requestedTrough: string;
  message: string;
  fees: SubmittedFee[];
  totalMinor: number | null;
  totalCurrency: string | null;
}

interface ValidationErrors {
  [key: string]: string[];
}

const SUBMITTED_STORAGE_KEY = (formId: string) =>
  `guestlist-request-submitted:${formId}`;

const confirmationStyles = `
@keyframes gl-fade-up {
  0%   { transform: translateY(8px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}
.gl-fade-up { animation: gl-fade-up 400ms ease-out both; }
`;

export const Route = createFileRoute('/request-guestlist/$guest-list-form-id')({
  component: GuestListRequestForm,
});

function GuestListRequestForm() {
  const { toast } = useToast();
  const { currentLanguage: lang } = useLanguage();
  const { 'guest-list-form-id': guestListFormId } = Route.useParams();
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    places: 1,
    requestedTrough: '',
    message: '',
  });
  const [selectedOptionalFeeIds, setSelectedOptionalFeeIds] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState<SubmittedRequest | null>(null);

  const fees = useMemo(() => formConfig?.fees ?? [], [formConfig?.fees]);
  const hasFees = fees.length > 0;

  const feeLineItems = useMemo(() => {
    return fees
      .filter((f) => f.fee.amountPerUnit !== null)
      .map((f) => {
        const applies = f.required || selectedOptionalFeeIds.includes(f.id);
        const perUnit = f.fee.amountPerUnit!;
        return {
          id: f.id,
          name: f.fee.name,
          required: f.required,
          applies,
          perUnit: Money.fromInteger(perUnit.amount, perUnit.currency),
        };
      });
  }, [fees, selectedOptionalFeeIds]);

  const currency = feeLineItems[0]?.perUnit.currency ?? 'EUR';

  const feeTotal = useMemo(() => {
    return feeLineItems
      .filter((item) => item.applies)
      .reduce(
        (acc, item) => acc.add(item.perUnit.multiply(formData.places)),
        Money.zero(currency),
      );
  }, [feeLineItems, formData.places, currency]);

  const toggleOptionalFee = (feeId: string) => {
    setSelectedOptionalFeeIds((prev) =>
      prev.includes(feeId) ? prev.filter((id) => id !== feeId) : [...prev, feeId],
    );
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SUBMITTED_STORAGE_KEY(guestListFormId));
      if (raw) setSubmitted(JSON.parse(raw) as SubmittedRequest);
    } catch {
      /* ignore */
    }
    fetchFormConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestListFormId]);

  const fetchFormConfig = async (): Promise<void> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/guest-list-request-form/${guestListFormId}`, {
        headers: {
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      setFormConfig(data.data);
      const optionalFeeIds = (data.data?.fees ?? [])
        .filter((f: FormFee) => !f.required && f.fee.amountPerUnit !== null)
        .map((f: FormFee) => f.id);
      setSelectedOptionalFeeIds(optionalFeeIds);
    } catch (error) {
      console.error('Failed to fetch form config:', error);
      setError('Failed to load form configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setValidationErrors({});
    setSubmitting(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/guest-list-request-form/${guestListFormId}/requests`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            request_id: ulid(),
            ...formData,
            selectedOptionalFeeIds,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 422) {
          setValidationErrors(errorData.errors || {});
          return;
        }
        throw new Error('Failed to submit form');
      }

      const submittedFees: SubmittedFee[] = feeLineItems
        .filter((item) => item.applies)
        .map((item) => ({
          name: item.name,
          amountPerUnit: {
            amount: item.perUnit.amount,
            currency: item.perUnit.currency,
          },
          required: item.required,
        }));

      const record: SubmittedRequest = {
        submittedAt: new Date().toISOString(),
        eventName: formConfig?.eventName ?? '',
        name: formData.name,
        email: formData.email,
        places: formData.places,
        requestedTrough: formData.requestedTrough,
        message: formData.message,
        fees: submittedFees,
        totalMinor: hasFees ? feeTotal.amount : null,
        totalCurrency: hasFees ? feeTotal.currency : null,
      };

      try {
        localStorage.setItem(
          SUBMITTED_STORAGE_KEY(guestListFormId),
          JSON.stringify(record),
        );
      } catch {
        /* ignore */
      }

      setSubmitted(record);

      setFormData({
        name: '',
        email: '',
        places: 1,
        requestedTrough: '',
        message: '',
      });
      setSelectedOptionalFeeIds(
        fees
          .filter((f) => !f.required && f.fee.amountPerUnit !== null)
          .map((f) => f.id),
      );

    } catch (error) {
      console.error('Failed to submit form:', error);
      setError('Failed to submit form');
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to submit form. Please try again.",
        className: "bg-red-900/50 border-red-500/30 text-white backdrop-blur-sm",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetForAnother = () => {
    try {
      localStorage.removeItem(SUBMITTED_STORAGE_KEY(guestListFormId));
    } catch {
      /* ignore */
    }
    setSubmitted(null);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof Omit<FormData, 'places'>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handlePlacesChange = (value: number) => {
    setFormData({ ...formData, places: value });
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="animate-pulse text-white/80">Loading...</div>
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-red-400">{error}</div>
    </div>
  );
  if (!formConfig) return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-white/80">Form not found</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white">
      <style>{confirmationStyles}</style>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header section */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-xs sm:text-sm font-medium text-white/70 uppercase tracking-[0.2em] mb-2 sm:mb-3">Guest List Request</h2>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 text-white">
            {formConfig.eventName}
          </h1>

          {!submitted && formConfig.instructions && (
            <div className="mb-6 sm:mb-8 prose prose-invert max-w-none prose-sm sm:prose-base">
              <ReactMarkdown>{formConfig.instructions}</ReactMarkdown>
            </div>
          )}
        </div>

        {submitted ? (
          <SubmittedConfirmation
            submitted={submitted}
            lang={lang}
            onReset={handleResetForAnother}
          />
        ) : (
          <div className="relative">
            {formConfig.disabled && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-10 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <div className="bg-red-900/30 border border-red-500/30 text-red-200 px-4 sm:px-6 py-3 sm:py-4 rounded-lg backdrop-blur-sm max-w-md mx-4 text-center text-sm sm:text-base">
                  {formConfig.disabled_reason}
                </div>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className={`space-y-6 sm:space-y-8 backdrop-blur-sm bg-white/5 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border border-white/10 ${
                formConfig.disabled ? 'opacity-50' : ''
              }`}
            >
              <div>
                <Label htmlFor="name" className="text-white/80 text-sm sm:text-base">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange(e, 'name')}
                  className={`bg-black/50 border-white/10 focus:border-white/30 focus:ring-white/20 mt-1.5 sm:mt-2 text-sm sm:text-base ${
                    validationErrors.name ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.name && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.name[0]}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email" className="text-white/80 text-sm sm:text-base">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange(e, 'email')}
                  className={`bg-black/50 border-white/10 focus:border-white/30 focus:ring-white/20 mt-1.5 sm:mt-2 text-sm sm:text-base ${
                    validationErrors.email ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.email && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.email[0]}</p>
                )}
              </div>

              <div>
                <Label htmlFor="places" className="text-white/80 text-sm sm:text-base">Number of Guestlist spots</Label>
                <StepperInput
                  id="places"
                  value={formData.places}
                  onChange={handlePlacesChange}
                  min={1}
                  max={formConfig.max_amount_for_guests_per_request}
                  className={`mt-1.5 sm:mt-2 ${
                    validationErrors.places ? ' [&_button]:border-red-500 [&_div]:border-red-500' : ''
                  }`}
                />
                {validationErrors.places && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.places[0]}</p>
                )}
              </div>

              <div>
                <Label htmlFor="requestedTrough" className="text-white/80 text-sm sm:text-base">Referred by</Label>
                <Input
                  id="requestedTrough"
                  value={formData.requestedTrough}
                  onChange={(e) => handleInputChange(e, 'requestedTrough')}
                  className={`bg-black/50 border-white/10 focus:border-white/30 focus:ring-white/20 mt-1.5 sm:mt-2 text-sm sm:text-base ${
                    validationErrors.requestedTrough ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.requestedTrough && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.requestedTrough[0]}</p>
                )}
              </div>

              <div>
                <Label htmlFor="message" className="text-white/80 text-sm sm:text-base">Additional Notes (optional)</Label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleInputChange(e, 'message')}
                  className={`w-full min-h-[80px] sm:min-h-[100px] px-3 sm:px-4 py-2 sm:py-3 mt-1.5 sm:mt-2 bg-black/50 border rounded-lg focus:ring-white/20 focus:border-white/30 text-sm sm:text-base ${
                    validationErrors.message ? 'border-red-500' : 'border-white/10'
                  }`}
                />
                {validationErrors.message && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.message[0]}</p>
                )}
              </div>

              {hasFees && (
                <div className="space-y-3 sm:space-y-4 pt-2">
                  <div>
                    <Label className="text-white/80 text-sm sm:text-base">Fees</Label>
                    {formConfig.feeDescription && (
                      <p className="text-xs sm:text-sm text-white/60 mt-1">
                        {formConfig.feeDescription}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    {feeLineItems.map((item) => {
                      const lineTotal = item.perUnit.multiply(formData.places);
                      return (
                        item.required ? (
                          <div
                            key={item.id}
                            className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-white/10 bg-black/40 text-sm sm:text-base opacity-80"
                          >
                            <div className="flex flex-col min-w-0">
                              <span className="text-white truncate">{item.name}</span>
                              <span className="text-xs text-white/60">
                                {item.perUnit.display(lang)} per spot · required
                              </span>
                            </div>
                            <span className="text-white font-medium shrink-0">
                              {lineTotal.display(lang)}
                            </span>
                          </div>
                        ) : (
                          <label
                            key={item.id}
                            className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-white/10 bg-black/40 text-sm sm:text-base cursor-pointer hover:bg-black/60"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <input
                                type="checkbox"
                                checked={item.applies}
                                onChange={() => toggleOptionalFee(item.id)}
                                className="h-4 w-4 rounded border-white/20 bg-black/50 accent-white"
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="text-white truncate">{item.name}</span>
                                <span className="text-xs text-white/60">
                                  {item.perUnit.display(lang)} per spot
                                </span>
                              </div>
                            </div>
                            <span className="text-white font-medium shrink-0">
                              {lineTotal.display(lang)}
                            </span>
                          </label>
                        )
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-white/10">
                    <span className="text-white/80 text-sm sm:text-base">
                      Total ({formData.places} {formData.places === 1 ? 'spot' : 'spots'})
                    </span>
                    <span className="text-white font-semibold text-base sm:text-lg">
                      {feeTotal.display(lang)}
                    </span>
                  </div>

                  <p className="text-xs text-white/50">
                    You will only be asked to pay after the organizer approves your request.
                  </p>
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-white hover:bg-white/90 text-black font-semibold py-4 sm:py-6 rounded-lg transition-all duration-300 text-sm sm:text-base"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing Request...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </form>
          </div>
        )}

        <div className="mt-8 sm:mt-16 pt-4 sm:pt-8 border-t border-white/10 text-center">
          <a href="https://fastlane.events" target="_blank" rel="noopener noreferrer" className="inline-block opacity-75 hover:opacity-100 transition-opacity">
            <p className="text-xs sm:text-sm text-white/60 mb-2 sm:mb-3">Powered by Fastlane</p>
          </a>
        </div>
      </div>
      <Toaster />
    </div>
  );
}

interface SubmittedConfirmationProps {
  submitted: SubmittedRequest;
  lang: string;
  onReset: () => void;
}

function SubmittedConfirmation({
  submitted,
  lang,
  onReset,
}: SubmittedConfirmationProps) {
  const total =
    submitted.totalMinor !== null && submitted.totalCurrency
      ? Money.fromInteger(submitted.totalMinor, submitted.totalCurrency)
      : null;

  return (
    <div className="backdrop-blur-sm bg-white/5 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border border-white/10">
      <div className="gl-fade-up mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-semibold mb-2 text-white">
          Request received
        </h2>
        <p className="text-white/70 text-sm sm:text-base">
          We'll email{' '}
          <span className="text-white font-medium">{submitted.email}</span> once
          the organizer reviews your request.
        </p>
      </div>

      <div
        className="gl-fade-up rounded-lg border border-white/10 bg-black/30 divide-y divide-white/10 text-sm sm:text-base"
        style={{ animationDelay: '100ms' }}
      >
        <DetailRow label="Name" value={submitted.name} />
        <DetailRow label="Email" value={submitted.email} />
        <DetailRow
          label="Spots"
          value={`${submitted.places} ${submitted.places === 1 ? 'spot' : 'spots'}`}
        />
        {submitted.requestedTrough && (
          <DetailRow label="Referred by" value={submitted.requestedTrough} />
        )}
        {submitted.message && (
          <DetailRow label="Notes" value={submitted.message} multiline />
        )}
        {submitted.fees.length > 0 && (
          <div className="px-3 sm:px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-white/50 mb-2">
              Fees
            </div>
            <ul className="space-y-1.5">
              {submitted.fees.map((f, idx) => {
                const perUnit = Money.fromInteger(
                  f.amountPerUnit.amount,
                  f.amountPerUnit.currency,
                );
                const line = perUnit.multiply(submitted.places);
                return (
                  <li
                    key={idx}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-white/80 truncate">
                      {f.name}
                      {f.required ? (
                        <span className="text-white/40"> · required</span>
                      ) : null}
                    </span>
                    <span className="text-white shrink-0">
                      {line.display(lang)}
                    </span>
                  </li>
                );
              })}
            </ul>
            {total && (
              <div className="flex items-center justify-between pt-3 mt-3 border-t border-white/10">
                <span className="text-white/80">Total if approved</span>
                <span className="text-white font-semibold">
                  {total.display(lang)}
                </span>
              </div>
            )}
            <p className="text-xs text-white/50 mt-3">
              You'll only be asked to pay once the organizer approves.
            </p>
          </div>
        )}
      </div>

      <div
        className="gl-fade-up flex justify-center mt-6 sm:mt-8"
        style={{ animationDelay: '200ms' }}
      >
        <button
          type="button"
          onClick={onReset}
          className="text-xs sm:text-sm text-white/60 hover:text-white underline underline-offset-4 decoration-white/20 hover:decoration-white transition-colors"
        >
          Submit another request
        </button>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div className="flex gap-3 px-3 sm:px-4 py-2.5">
      <div className="text-xs uppercase tracking-wider text-white/50 w-24 shrink-0 pt-0.5">
        {label}
      </div>
      <div
        className={`text-white/90 ${multiline ? 'whitespace-pre-wrap break-words' : 'truncate'}`}
      >
        {value}
      </div>
    </div>
  );
}
